import React, { useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { ActionMetadataOutDTO, ActionParamMetadataOutDTO } from '@api/ringil-api.v2';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { ActionCall, RuleValue } from '../../ruleAst';
import { useRuleMetadata } from '../../metadata/RuleMetadataProvider';
import { ScalarInput } from './ValueInput';
import { descriptorOfParam } from './valueDescriptor';
import { paramLabel } from '../../i18nKeys';
import { AMOUNT_MODE_OPTIONS, AMOUNT_MODE_PARAMS, AmountMode, amountModeHint, amountModeOf, hasAmountMode, withAmountMode } from './amountMode';
import { hasPriceMode, PRICE, PRICE_MODE_PARAMS } from './priceTariff';
import PriceSection from './PriceSection';

interface ComponentProps {
  actions: ActionCall[];
  available: ActionMetadataOutDTO[];
  onChange: (actions: ActionCall[]) => void;
  companyId?: string;
  readOnly?: boolean;
}

/**
 * The ordered list of actions a rule runs when it matches.
 *
 * The catalogue comes from metadata, so the `recommend_` / `distribute_to_best_` and `propose_` /
 * `award_` pairs appear as what they are — separate actions the author picks between — rather than
 * as a mode toggle, which is the design this replaced.
 */
const ActionList: React.FC<ComponentProps> = ({ actions, available, onChange, companyId, readOnly }) => {
  const { tr } = useTranslationLgs();
  const { actionByKey } = useRuleMetadata();

  const options = available.map(action => ({ label: tr(action.i18nKey, action.key), value: action.key }));

  const changeAction = (index: number, actionKey: string) => {
    const metadata = actionByKey(actionKey);
    // Prefill whatever the catalogue declares a default for; the rest start empty.
    const params: Record<string, RuleValue> = {};
    (metadata?.params ?? []).forEach(param => {
      if (param.defaultValue !== null && param.defaultValue !== undefined) params[param.name] = param.defaultValue as RuleValue;
    });
    // `of: ORIGINAL` is a default, but only meaningful beside a percentage — start from "Fixed".
    const prefilled = hasAmountMode(metadata?.params) ? withAmountMode(params, 'FIXED') : params;
    onChange(actions.map((action, i) => (i === index ? { action: actionKey, params: prefilled } : action)));
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= actions.length) return;
    const next = [...actions];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-3">
      {actions.length === 0 && <span className="text-content-secondary">{tr('AiDispatcher.noActions', 'This rule does nothing yet — add an action.')}</span>}

      {actions.map((action, index) => {
        const metadata = actionByKey(action.action);
        return (
          <div key={index} className="rounded-md border border-solid border-base-tertiary bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-content-secondary">{index + 1}.</span>
              <div className="min-w-[18rem] flex-1">
                <Dropdown
                  className="w-full"
                  value={action.action || null}
                  options={options}
                  onChange={event => changeAction(index, event.value)}
                  disabled={readOnly}
                  filter={options.length > 10}
                  placeholder={tr('AiDispatcher.selectAction', 'Select an action')}
                />
              </div>
              {!readOnly && (
                <div className="flex items-center gap-1">
                  <RingilButton
                    mode="text-only"
                    label={tr('AiDispatcher.moveUp', 'Up')}
                    tooltip={tr('AiDispatcher.moveUpTooltip', 'Run this action earlier')}
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                  />
                  <RingilButton
                    mode="text-only"
                    label={tr('AiDispatcher.moveDown', 'Down')}
                    tooltip={tr('AiDispatcher.moveDownTooltip', 'Run this action later')}
                    onClick={() => move(index, 1)}
                    disabled={index === actions.length - 1}
                  />
                  <RingilButton mode="text-only" label={tr('AiDispatcher.remove', 'Remove')} onClick={() => onChange(actions.filter((_, i) => i !== index))} />
                </div>
              )}
            </div>

            {(metadata?.params ?? []).length > 0 && (
              <ActionParams
                key={action.action}
                action={action}
                params={metadata.params}
                onChange={params => onChange(actions.map((current, i) => (i === index ? { ...current, params } : current)))}
                companyId={companyId}
                readOnly={readOnly}
              />
            )}
          </div>
        );
      })}

      {!readOnly && (
        <RingilButton mode="text-only" label={tr('AiDispatcher.addAction', '+ Add an action')} onClick={() => onChange([...actions, { action: '', params: {} }])} />
      )}
    </div>
  );
};

interface ActionParamsProps {
  action: ActionCall;
  params: ActionParamMetadataOutDTO[];
  onChange: (params: Record<string, RuleValue>) => void;
  companyId?: string;
  readOnly?: boolean;
}

/**
 * One action's parameter fields. An action taking `amount` *or* `percent` (`add_extra_cost`) gets a
 * "How much" switch in place of those fields, so an author cannot fill both or neither — the
 * backend only refuses that at run time. An action taking a `price` and a `price_tariff` gets a
 * "Price" section in their place, for the same reason.
 */
const ActionParams: React.FC<ActionParamsProps> = ({ action, params, onChange, companyId, readOnly }) => {
  const { tr } = useTranslationLgs();
  // Remembers the choice while neither value is entered yet, when the parameters cannot say it.
  const [chosenMode, setChosenMode] = useState<AmountMode>('FIXED');
  const withSwitch = hasAmountMode(params);
  const withPrice = hasPriceMode(params);
  const mode = amountModeOf(action.params, chosenMode);
  const byName = (name: string) => params.find(param => param.name === name);

  const changeParam = (name: string, value: RuleValue) => onChange({ ...action.params, [name]: value });

  const field = (param: ActionParamMetadataOutDTO, descriptor = descriptorOfParam(param, action.action), required = param.required) => (
    // A div, not a <label>: a label forwards every click inside it to its first control, so the
    // "Create new label" link and the form under a label picker would open the picker as well.
    <div key={param.name} className="flex flex-col gap-1">
      <span className="text-sm text-content-secondary">
        {paramLabel(tr, param.name)}
        {required && <span className="ml-1 text-content-brand">*</span>}
      </span>
      <ScalarInput
        descriptor={descriptor}
        value={(action.params?.[param.name] as RuleValue) ?? null}
        onChange={value => changeParam(param.name, value)}
        companyId={companyId}
        disabled={readOnly}
      />
    </div>
  );

  const hint = amountModeHint(mode);
  const valueParam = byName(mode === 'FIXED' ? 'amount' : 'percent');

  return (
    <div className="mt-3 grid grid-cols-1 gap-3 pl-6 md:grid-cols-2">
      {params.map(param => {
        // The price section stands where `price` is declared; `price_tariff` renders nothing of its own.
        if (withPrice && PRICE_MODE_PARAMS.includes(param.name)) {
          if (param.name !== PRICE) return null;
          return <PriceSection key="price" action={action.action} params={action.params ?? {}} onChange={onChange} companyId={companyId} readOnly={readOnly} />;
        }
        if (!withSwitch || !AMOUNT_MODE_PARAMS.includes(param.name)) return field(param);
        // The switch and its one value field stand where `amount` is declared; `percent` and `of` render nothing of their own.
        if (param.name !== 'amount') return null;
        return (
          <React.Fragment key="amountMode">
            <div className="flex flex-col gap-1 md:col-span-2">
              <span className="text-sm text-content-secondary">
                {tr('AiDispatcher.amountMode.label', 'How much')}
                <span className="ml-1 text-content-brand">*</span>
              </span>
              <InputSelect
                model={AMOUNT_MODE_OPTIONS.map(option => ({ code: option.mode, label: tr(option.label.key, option.label.text) }))}
                selectedItemCode={mode}
                onSelect={code => {
                  setChosenMode(code as AmountMode);
                  onChange(withAmountMode(action.params, code as AmountMode));
                }}
                disabled={readOnly}
                showCheckIcon={false}
              />
              <span className="text-xs text-content-tertiary">{tr(hint.key, hint.text)}</span>
            </div>
            {valueParam && field(valueParam, mode === 'FIXED' ? undefined : { ...descriptorOfParam(valueParam, action.action), unit: '%' }, true)}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ActionList;
